// Pure derivation of per-datasheet props from a clinical bundle (see
// services/clinicalBundle.fetchClinicalBundle). This mirrors the logic the
// therapist data-collection page applies inline (pages/therapist/data-collection.jsx
// → applyClinicalData) so the SUPERVISOR / inclusion-head session screen
// (sections/shared/data-collection/PersonaSessionDataCollection.jsx) renders the
// SAME real IEP data instead of demo placeholders.
//
// Keep this in sync with applyClinicalData: the shape helpers, the MAND-domain /
// tracking-status routing, and the fallback behaviour must stay identical.

import { transformTargetForColdProbe, transformTargetForTracking, transformTargetForMaintenance } from 'utils/apiTransformers';

// Tracking phases that route a target to the Maintenance Probe sheet instead of
// the active Cold Probe / Daily Mand sheets.
const PROBE_TRACKING_STATUSES = new Set(['weekly_maintenance', 'monthly', 'long_term']);

// Services whose goals belong to their OWN data sheet, not to these.
//
// Cold Probe, Multi-Trial, Daily Mand, Maintenance Probe and Target Tracking are
// ABA sheets: they score a target trial by trial against a prompt level. A speech
// or occupational-therapy goal is worked through session activities and scored on
// that service's own sheet, which seeds itself from the child's goals for that
// service (see utils/iepServiceGoals). A therapy goal appearing here would be
// scored twice, in two different clinical languages.
//
// A goal with NO service is left in. Every goal authored before the service field
// existed has none, and dropping those would empty these sheets for every child
// already on the platform.
const THERAPY_SERVICES = new Set(['speech_therapy', 'occupational_therapy']);
const isTherapyGoal = (goal) => THERAPY_SERVICES.has(String(goal?.serviceCode || goal?.service_code || '').trim());

// The key a behavior's recorded occurrences hang off. IEP-form behaviors may carry
// no explicit id, so one is derived from position + name. Shared with the session
// Behaviors dialog, which pins this key onto a behavior it edits so a rename does
// not detach the counts already recorded against it.
export const behaviorKey = (b, idx) => b?.id || `iep-behavior-${idx}-${(b?.behaviorName || '').toLowerCase().replace(/\s+/g, '-')}`;

// Behaviors for Behavior Tracking / Behavior Interval. Prefer the IEP-snapshot
// behaviors the supervisor tracks for this child; fall back to org-wide ref
// behaviors only when the snapshot has none.
export const deriveBehaviorList = (behaviorsRes, iepBehaviorsSnapshot) => {
  const fromIep = Array.isArray(iepBehaviorsSnapshot) ? iepBehaviorsSnapshot : [];
  if (fromIep.length > 0) {
    return fromIep.map((b, idx) => {
      const m = b.measurement || {};
      const tracking = [];
      if (m.trackFrequency) tracking.push('frequency');
      if (m.trackDuration) tracking.push('duration');
      if (tracking.length === 0) tracking.push('frequency');
      return {
        // IEP-form behaviors don't carry an explicit id; derive a stable one so
        // each card in BehaviorTrackingMode is independent.
        id: behaviorKey(b, idx),
        name: b.behaviorName || b.name || '',
        code: b.code,
        behavior_type: b.behavior_type,
        settings: b.settings || '',
        tracking
      };
    });
  }
  return (behaviorsRes || []).map((b) => ({
    id: b.id,
    name: b.name,
    code: b.code,
    behavior_type: b.behavior_type,
    tracking: b.measurement_type === 'duration' ? ['duration'] : b.measurement_type === 'both' ? ['frequency', 'duration'] : ['frequency']
  }));
};

/**
 * Derive the per-datasheet props from a clinical bundle.
 * @param {object} bundle - result of fetchClinicalBundle()
 * @returns {{
 *   mandItems: object[], mandTargets: object[], coldProbeTargets: object[],
 *   multiTrialTargets: object[], maintenanceTargets: object[],
 *   targetTrackingTargets: object[], behaviorList: object[],
 *   addTargetCtx: {iepId: string, goals: object[]}, hasIep: boolean
 * }}
 */
export const deriveDatasheetProps = (bundle) => {
  const { iepData, behaviors: behaviorsRes, iepBehaviorsSnapshot, iepSnapshot, iepTargetsOverlay, maintenanceHistory = {} } = bundle || {};

  const behaviorList = deriveBehaviorList(behaviorsRes, iepBehaviorsSnapshot);

  // Goals + targets the supervisor edits live in the IEP version SNAPSHOT
  // (iepGoals) plus a per-goal targets overlay. Trust the snapshot when present;
  // otherwise fall back to the active-IEP goals table.
  const snapshotGoals = Array.isArray(iepSnapshot?.iepGoals) ? iepSnapshot.iepGoals : [];
  const useSnapshot = snapshotGoals.length > 0;
  const allTargets = useSnapshot
    ? snapshotGoals
        .filter((g) => !isTherapyGoal(g))
        .flatMap((g) => {
          const ts = (iepTargetsOverlay && iepTargetsOverlay[g.goalId]) || [];
          const domain = g.domain || g.title || g.description || '';
          return ts.map((t) => ({ ...t, _goalDomain: domain }));
        })
    : (iepData?.goals || [])
        .filter((g) => !isTherapyGoal(g))
        .flatMap((g) => (g.targets || []).map((t) => ({ ...t, _goalDomain: g.skill_domain_code || g.title || g.description || '' })));
  const hasSnapshotTargets = useSnapshot && allTargets.length > 0;

  // Cold probe / Multi Trial — targets actively being taught.
  const acquisitionTargets = allTargets.filter((t) => {
    const st = (t.status?.name || t.status || '').toLowerCase();
    return st === 'acquisition' || st === 'in_progress' || st === 'baseline';
  });

  const isMandDomain = (t) => (t._goalDomain || '').toLowerCase().includes('mand');
  const trackingOf = (t) => (t.trackingStatus || t.tracking_status || '').toLowerCase();
  const isProbeMaintenance = (t) => PROBE_TRACKING_STATUSES.has(trackingOf(t));

  const nonMandAcquisitionTargets = acquisitionTargets.filter((t) => !isMandDomain(t) && !isProbeMaintenance(t));

  // Snapshot targets already match the cold-probe shape; active-IEP targets need
  // the API transformer.
  const toColdProbeShape = (t) =>
    hasSnapshotTargets
      ? {
          id: t.id,
          name: t.name || t.description,
          description: t.description || '',
          domain: t._goalDomain || '',
          image: null,
          materials: Array.isArray(t.materials) ? t.materials : [],
          // Pre-selects the target's usual controlling prompt in the session sheets.
          defaultControllingPrompts: Array.isArray(t.defaultControllingPrompts) ? t.defaultControllingPrompts : [],
          // SD (discriminative stimulus) — shown as a tooltip against the target.
          sdInstruction: t.sdInstruction || ''
        }
      : transformTargetForColdProbe(t, t._goalDomain);

  // Cold Probe / Multi-Trial show only the child's real non-MAND acquisition
  // targets. When there are none, show empty (the IEP needs non-MAND targets) —
  // never fall back to demo/placeholder data.
  const coldProbeTargets = nonMandAcquisitionTargets.length > 0 ? nonMandAcquisitionTargets.map(toColdProbeShape) : [];

  // Maintenance Probe = weekly / monthly / long-term maintenance targets.
  const maintenanceTargets = allTargets.filter(isProbeMaintenance).map((t) =>
    hasSnapshotTargets
      ? {
          id: t.id,
          name: t.name || t.description,
          description: t.description || '',
          domain: t._goalDomain,
          masteredDate: t.dateMastered || null,
          lastMastered: t.dateMastered || null,
          status: t.status,
          trackingStatus: trackingOf(t),
          lastProbeDate: maintenanceHistory[t.id]?.lastProbeDate || null,
          lastProbeScore: maintenanceHistory[t.id]?.lastProbeScore || null
        }
      : {
          ...transformTargetForMaintenance(t, t._goalDomain),
          trackingStatus: trackingOf(t),
          lastProbeDate: maintenanceHistory[t.id]?.lastProbeDate || null,
          lastProbeScore: maintenanceHistory[t.id]?.lastProbeScore || null
        }
  );

  // Target/skill tracking = all targets.
  const targetTrackingTargets = allTargets.map((t) =>
    hasSnapshotTargets
      ? {
          id: t.id,
          target: t.description || t.name,
          domain: t._goalDomain,
          dateIntroduced: t.dateIntroduced || null,
          dateMastered: t.dateMastered || null,
          daysToMastery:
            t.dateIntroduced && t.dateMastered
              ? Math.max(0, Math.round((new Date(t.dateMastered) - new Date(t.dateIntroduced)) / 86400000))
              : '-',
          location: t.location || '',
          people: t.people || '',
          stimuli: t.stimuli || ''
        }
      : transformTargetForTracking(t, t._goalDomain)
  );

  // Daily Mand = MAND-domain targets not in weekly/monthly/long-term maintenance.
  // Carries dateIntroduced/dateMastered so DailyMandMode can keep only targets
  // currently in progress (introduced & not yet mastered).
  const toMandTargetShape = (t) => {
    const base = toColdProbeShape(t);
    const dateIntroduced = hasSnapshotTargets ? t.dateIntroduced || null : t.start_date || (t._raw && t._raw.start_date) || null;
    const dateMastered = hasSnapshotTargets ? t.dateMastered || null : t.achieved_date || (t._raw && t._raw.achieved_date) || null;
    return { ...base, dateIntroduced, dateMastered };
  };
  const mandTargets = allTargets.filter((t) => isMandDomain(t) && !isProbeMaintenance(t)).map(toMandTargetShape);
  const mandItems = mandTargets.map((t) => ({ ...t, category: 'mand' }));

  // Multi Trial reuses cold-probe targets with a default trial count.
  const multiTrialTargets = coldProbeTargets.map((t) => ({ ...t, trials: t.trials || 10 }));

  return {
    mandItems,
    mandTargets,
    coldProbeTargets,
    multiTrialTargets,
    maintenanceTargets,
    targetTrackingTargets,
    // Target Tracking sheet: every non-declined target with its raw row (mastered too).
    trackingTargets: Array.isArray(bundle?.trackingTargets) ? bundle.trackingTargets : [],
    behaviorList,
    addTargetCtx: bundle?.addTargetCtx || { iepId: '', goals: [] },
    hasIep: hasSnapshotTargets || (iepData?.goals || []).length > 0
  };
};

export default deriveDatasheetProps;
